import React, { useState, useMemo } from 'react'
import { IEKGEvent } from '../store/ekgImport.types'
import { IonCol, IonGrid, IonIcon, IonItem, IonRow, IonToggle } from '@ionic/react'
import LabelComponent from '../../../components/label/Label.component'
import { convertISOToHourAndMinute } from '../../../utilities/ekgImport.util'
import { chevronDown, chevronUp } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import './EventItem.component.css'
import { ITempMethodOptions } from '../../vitals/store/vitals.types'
import { useWindowSize } from '../../../hooks/useWindowSize'

interface IEventItemProps {
  event: IEKGEvent
  showToggle?: boolean
  isChecked?: boolean
  handleToggle?: (id: string) => void
  temperatureMethodOptions: ITempMethodOptions
}

const allVitals = {
  heartRate: 'ekg.heartRate',
  systolicBP: 'ekg.SBP',
  diastolicBP: 'ekg.DBP',
  mapVital: 'ekg.MAP',
  spo2: 'ekg.SpO2',
  etCO2: 'ekg.EtCo2',
  respirationRate: 'ekg.Resp',
  spCO: 'ekg.SpCo',
  spMet: 'ekg.SpMet',
  temperature: 'ekg.Temp',
}

const getVisibleColumns = (width: number): Set<string> => {
  const visible = new Set<string>(['heartRate', 'systolicBP'])

  if (width >= 620) visible.add('diastolicBP')
  if (width >= 640) visible.add('mapVital')
  if (width >= 900) {
    visible.add('spo2')
    visible.add('etCO2')
  }
  if (width >= 1024) visible.add('respirationRate')
  if (width >= 1180) {
    visible.add('spCO')
    visible.add('spMet')
  }
  if (width >= 1280) {
    visible.add('temperature')
  }

  return visible
}

const EventItem = (props: IEventItemProps) => {
  const { event, showToggle, isChecked, handleToggle, temperatureMethodOptions } = props
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const { width: windowWidth } = useWindowSize()

  const onClick = () => {
    setIsExpanded((prev) => !prev)
  }

  const visibleColumns = getVisibleColumns(windowWidth)
  const hiddenVitals = Object.keys(allVitals).filter((vital) => !visibleColumns.has(vital))

  const allowExpand = hiddenVitals.some((vital) => {
    const value = event[vital as keyof IEKGEvent]
    return value != null && value !== ''
  })

  const renderTag = (label: string, value: string) => {
    return (
      <div className='event-item-tag'>
        <div>{label}</div>
        <div className='event-item-tag-value'>{value}</div>
      </div>
    )
  }

  const tempMethod = useMemo(
    () =>
      temperatureMethodOptions?.length && event.temperatureMethod
        ? temperatureMethodOptions.find((option) => option.id === Number(event.temperatureMethod))
        : null,
    [temperatureMethodOptions, event]
  )

  const renderOtherVitals = () => {
    return (
      <>
        {hiddenVitals.map((vitalName) => {
          let vitalValue = event[vitalName as keyof IEKGEvent]
          if (vitalValue == null || vitalValue === '') return null

          const vitalLabel = t(allVitals[vitalName as keyof typeof allVitals])

          if (vitalName === 'temperature') {
            const tempType = event.temperatureType || 'C'

            const tempMethodValue = tempMethod?.name ? `${tempMethod.name}` : ''
            vitalValue = `${vitalValue} ${tempType}° ${tempMethodValue}`
          }

          return <React.Fragment key={vitalName}>{renderTag(vitalLabel, vitalValue as string)}</React.Fragment>
        })}
      </>
    )
  }

  const gridClassName = showToggle ? 'event-item-grid' : 'event-item-grid-summary'

  return (
    <IonItem className='event-item'>
      <div className='event-item-main'>
        {showToggle && handleToggle && (
          <IonToggle
            data-testid={`Toggle-${event.id}`}
            slot='start'
            checked={isChecked}
            onClick={() => handleToggle(event.id.toString())}
          />
        )}

        <IonGrid className={gridClassName}>
          <IonRow className='event-item-row'>
            <IonCol className='data-point event-time'>
              <LabelComponent isPHI label={convertISOToHourAndMinute(event.dtDevice)} size={3} />
            </IonCol>
            <IonCol className='data-point event-description'>
              <LabelComponent isPHI label={event.description} size={3} />
            </IonCol>
            <IonCol className='data-point event-heartRate'>
              <LabelComponent isPHI label={event.heartRate || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-systolicBP'>
              <LabelComponent isPHI label={event.systolicBP || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-diastolicBP'>
              <LabelComponent isPHI label={event.diastolicBP || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-map'>
              <LabelComponent isPHI label={event.mapVital || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-spo2'>
              <LabelComponent isPHI label={event.spo2 || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-etco2'>
              <LabelComponent isPHI label={event.etCO2 || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-resp'>
              <LabelComponent isPHI label={event.respirationRate || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-spco'>
              <LabelComponent isPHI label={event.spCO || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-spmet'>
              <LabelComponent isPHI label={event.spMet || ''} size={3} />
            </IonCol>
            <IonCol className='data-point event-temp'>
              <LabelComponent
                isPHI
                label={
                  event.temperature
                    ? `${event.temperature} ${event.temperatureType || 'C'}°` +
                      (tempMethod?.name ? `\n${tempMethod.name}` : '')
                    : ''
                }
                size={3}
              />
            </IonCol>
            {allowExpand && (
              <IonCol className='data-point event-more' onClick={onClick}>
                <IonIcon color='medium' className='event-more-icon' icon={isExpanded ? chevronUp : chevronDown} />
              </IonCol>
            )}
          </IonRow>
        </IonGrid>
      </div>
      {allowExpand && isExpanded && <div className='event-item-tags'>{renderOtherVitals()}</div>}
    </IonItem>
  )
}

export default EventItem
